import type { EpisodeGenerateQueueState } from '../comicEpisodeGenerateQueue'
import type { DynamicIslandActivity } from '../../context/dynamicIslandTypes'
import {
  comicEpisodeQueueIslandPhaseLabel,
  comicEpisodeQueueIslandProgressPercent,
  deriveComicEpisodeQueueIslandPhase,
  formatComicEpisodeQueueIslandProgress,
} from '../comicEpisodeQueueIsland'
import { episodeQueueIslandPhaseRailSemantic } from '../episodeQueueTimelineTheme'

export type ComicQueueIslandHandlers = {
  onOpenDetail: () => void
  onClosePanel?: () => void
  detailTitle?: string
  renderDetail?: () => import('react').ReactNode
}

function phaseToAccent(
  phase: ReturnType<typeof deriveComicEpisodeQueueIslandPhase>,
): DynamicIslandActivity['compact'] extends { accent: infer A } ? A : never {
  const semantic = episodeQueueIslandPhaseRailSemantic(phase)
  if (semantic === 'success') return 'success'
  if (semantic === 'danger') return 'danger'
  if (semantic === 'cancelled') return 'cancelled'
  if (semantic === 'warn') return 'warn'
  return 'muted'
}

export function mapComicQueueToActivity(
  queue: EpisodeGenerateQueueState,
  handlers: ComicQueueIslandHandlers,
): DynamicIslandActivity {
  const phase = deriveComicEpisodeQueueIslandPhase(queue)
  const phaseLabel = comicEpisodeQueueIslandPhaseLabel(phase)
  const { compactSubtitle, expandedDescription } = formatComicEpisodeQueueIslandProgress(queue)
  const progressPercent = comicEpisodeQueueIslandProgressPercent(queue)
  const accent = phaseToAccent(phase)
  const terminal = phase === 'success' || phase === 'warning' || phase === 'cancelled'
  const title = queue.episodeTitle
  const expandedTitle = `生成本话 · ${title}`

  const closeAction: DynamicIslandActivity['actions'][number] = {
    id: 'close',
    label: '关闭',
    onClick: handlers.onClosePanel ?? (() => {}),
  }

  const actions: DynamicIslandActivity['actions'] = [
    {
      id: 'detail',
      label: '详情',
      onClick: handlers.onOpenDetail,
    },
    closeAction,
  ]

  if (terminal) {
    return {
      id: 'comic-episode-queue',
      sourceKind: 'comic.episode-generate',
      sourceLabel: '漫画',
      priority: 10,
      lifecycle: 'terminal',
      compact: {
        mode: 'terminal',
        title,
        hint: compactSubtitle,
        accent,
      },
      expanded: {
        mode: 'summary',
        title: expandedTitle,
        description: expandedDescription,
        progressPercent,
        accent,
      },
      actions,
      detailTitle: expandedTitle,
      renderDetail: handlers.renderDetail,
    }
  }

  return {
    id: 'comic-episode-queue',
    sourceKind: 'comic.episode-generate',
    sourceLabel: '漫画',
    priority: 10,
    lifecycle: 'active',
    compact: {
      mode: 'progress',
      title,
      subtitle: compactSubtitle,
      progressPercent,
      accent,
      statusLabel: phaseLabel,
    },
    expanded: {
      mode: 'summary',
      title: expandedTitle,
      description: expandedDescription,
      progressPercent,
      accent,
      statusLabel: phaseLabel,
    },
    actions,
    detailTitle: expandedTitle,
    renderDetail: handlers.renderDetail,
  }
}
